import type { ModuleSearchResult } from '@/lib/api/search'
import type { CaptionModelStatus } from '@/lib/utils/tauri-recording'
import { useEffect, useMemo, useRef, useState } from 'react'
import { RecordingPermissionsDialog } from '@/components/desktop/RecordingPermissionsDialog'
import { SummaryTemplateSelect } from '@/components/meetings/SummaryTemplateSelect'
import { AICreditNotice } from '@/components/shared/ai/AICreditNotice'
import { useTheme } from '@/components/theme-provider'
import { LinkWorkSelector } from '@/components/time-entry/LinkWorkSelector'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAICredits } from '@/hooks/useAICredits'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useResolvedDarkMode } from '@/hooks/useResolvedDarkMode'
import { meetingRecordingsApi } from '@/lib/api/meeting-recordings.api'
import { meetingSummaryTemplatesApi } from '@/lib/api/meeting-summary-templates.api'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import {
  checkRecordingPermissions,
  downloadCaptionModel,
  getCaptionModelStatus,
  getRecordingSupport,
  getSystemAudioStatus,
  listenForCaptionModelProgress,
  pauseRecording,
  resolveLiveCaptionsEnabled,
  resumeRecording,
  startRecording,
  stopRecording,
  updateRecordingLink,
  updateRecordingTemplate,
} from '@/lib/utils/tauri-recording'
import {
  fileExtensionForMimeType,
  isWebRecordingSupported,
  WebMicRecorder,
} from '@/lib/utils/web-recorder'
import {
  clearStoredScratchpad,
  isLiveCaptionsPreferred,
  setLiveCaptionsPreferred,
  useRecordingStore,
} from '@/store/recording.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import {
  CircleNotchIcon,
  PauseIcon,
  PencilSimpleLineIcon,
  PlayIcon,
  RecordIcon,
  StopIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { ModuleTypeEnum } from '@scrambl/shared'

// Hard cap on a single recording session (matches MAX_RECORDING_MS in the
// desktop Rust recorder) — auto-stops so a forgotten recording can't run all
// day. The audio captured so far is still uploaded and processed.
const MAX_RECORDING_MS = 2 * 60 * 60 * 1000

function formatElapsed(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000)
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

function VuBars({ level }: { level: number }) {
  const bars = 5
  const active = Math.min(bars, Math.round(level * bars * 4))
  return (
    <div className="flex items-end gap-0.5" aria-hidden>
      {Array.from({ length: bars }, (_, index) => (
        <span
          key={index}
          className={cn(
            'w-0.5 rounded-full transition-colors',
            index < active ? 'bg-red-500' : 'bg-current opacity-25',
          )}
          style={{ height: `${5 + index * 2}px` }}
        />
      ))}
    </div>
  )
}

/**
 * Title-bar record widget — mirrors the TimeEntryWidget pattern. Idle:
 * record button + popover with an optional module picker ("which record
 * should the AI note be tied to"). Recording: red dot, elapsed time, VU
 * meter, pause/stop controls.
 *
 * Desktop (Tauri): full capture via the native recorder (mic + system
 * audio). Web browsers: limited support — mic-only via MediaRecorder, with
 * a hint to use the desktop app for the full experience.
 */
export function RecordButton() {
  const state = useRecordingStore(s => s.state)
  const durationMs = useRecordingStore(s => s.durationMs)
  const levels = useRecordingStore(s => s.levels)
  const systemAudioCaptured = useRecordingStore(s => s.systemAudioCaptured)
  const linkedModule = useRecordingStore(s => s.linkedModule)
  const setLinkedModule = useRecordingStore(s => s.setLinkedModule)
  const summaryTemplate = useRecordingStore(s => s.summaryTemplate)
  const setSummaryTemplate = useRecordingStore(s => s.setSummaryTemplate)
  const activeEventKey = useRecordingStore(s => s.activeEventKey)
  const scratchpad = useRecordingStore(s => s.scratchpad)
  const captions = useRecordingStore(s => s.captions)
  const captionsActive = useRecordingStore(s => s.captionsActive)

  // Live captions preference + on-device model state (desktop only)
  const [captionModel, setCaptionModel] = useState<CaptionModelStatus | null>(
    null,
  )
  const [captionDownloadPct, setCaptionDownloadPct] = useState<number | null>(
    null,
  )
  const [liveCaptionsEnabled, setLiveCaptionsEnabled] = useState(
    isLiveCaptionsPreferred,
  )
  const [catchUpOpen, setCatchUpOpen] = useState(false)
  const [catchUpSummary, setCatchUpSummary] = useState<string | null>(null)
  const [isCatchingUp, setIsCatchingUp] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isSupported, setIsSupported] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)
  const { checkFeatureFlag } = useFeatureFlags()

  // Same pill styling as the timer widget next to this button (adapts to
  // the desktop title-bar theme; plain dark-on-light in the web header)
  const isMobile = useIsMobile()
  const { theme } = useTheme()
  const sidebarTheme = useUIPreferencesStore(s => s.sidebarTheme)
  const isDarkMode = useResolvedDarkMode(theme)
  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]
  const useDarkText =
    isTauri() && !isMobile
      ? !isDarkMode && themeConfig.lightTextColor === 'dark'
      : !isDarkMode

  const isDesktop = isTauri()
  const queryClient = useQueryClient()
  const webRecorderRef = useRef<WebMicRecorder | null>(null)
  const webRecordingIdRef = useRef<string | null>(null)
  const systemAudioModeRef = useRef<string>('none')
  const applyStatePayload = useRecordingStore(s => s.applyStatePayload)
  const setLevels = useRecordingStore(s => s.setLevels)

  useEffect(() => {
    if (isTauri()) {
      void getRecordingSupport().then(support => {
        setIsSupported(support.supported)
        systemAudioModeRef.current = support.systemAudioMode
      })
      void getCaptionModelStatus().then(setCaptionModel)
    } else {
      setIsSupported(isWebRecordingSupported())
    }
  }, [])

  const handleCaptionsToggle = async (checked: boolean) => {
    if (!checked) {
      setLiveCaptionsEnabled(false)
      setLiveCaptionsPreferred(false)
      return
    }
    if (captionModel?.state === 'ready') {
      setLiveCaptionsEnabled(true)
      setLiveCaptionsPreferred(true)
      return
    }
    // First enable: download the on-device model (one-time, ~142MB)
    if (captionModel?.state === 'downloading' || captionDownloadPct !== null) {
      return
    }
    setCaptionDownloadPct(0)
    const unlisten = await listenForCaptionModelProgress(progress => {
      if (progress.totalBytes > 0) {
        setCaptionDownloadPct(
          Math.round((progress.downloadedBytes / progress.totalBytes) * 100),
        )
      }
    })
    try {
      await downloadCaptionModel()
      setCaptionModel({ supported: true, state: 'ready' })
      setLiveCaptionsEnabled(true)
      setLiveCaptionsPreferred(true)
      toast.success('Live captions ready')
    } catch (error) {
      toast.error('Caption model download failed', {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 160),
      })
    } finally {
      unlisten()
      setCaptionDownloadPct(null)
    }
  }

  const handleCatchUp = async () => {
    if (catchUpCredits?.canUse === false) {
      toast.error('Not enough AI credits for a catch-up summary')
      return
    }
    const transcript = useRecordingStore
      .getState()
      .captions.map(c =>
        c.speaker === 'you'
          ? `You: ${c.text}`
          : c.speaker === 'others'
            ? `Them: ${c.text}`
            : c.text,
      )
      .join('\n')
    setCatchUpOpen(true)
    setIsCatchingUp(true)
    setCatchUpSummary(null)
    try {
      const { summary } = await meetingRecordingsApi.catchUp(transcript)
      setCatchUpSummary(summary)
      void refetchCatchUpCredits()
    } catch {
      setCatchUpSummary(
        "Couldn't generate a catch-up summary — try again in a moment.",
      )
      void refetchCatchUpCredits()
    } finally {
      setIsCatchingUp(false)
    }
  }

  // Stop an in-flight web recording when the component unmounts
  useEffect(() => {
    return () => webRecorderRef.current?.cancel()
  }, [])

  const isRecording = state === 'recording'
  const isPaused = state === 'paused'
  const isActive = isRecording || isPaused || state === 'finalizing'
  const { data: catchUpCredits, refetch: refetchCatchUpCredits } = useAICredits(
    'meeting_catch_up',
    isActive,
  )

  const linkValue = useMemo(
    () =>
      linkedModule
        ? {
            moduleType: linkedModule.moduleType as ModuleTypeEnum,
            moduleId: linkedModule.moduleId,
            moduleName: linkedModule.moduleName,
          }
        : null,
    [linkedModule],
  )

  const handleModuleSelect = (result: ModuleSearchResult | null) => {
    const next = result
      ? {
          moduleType: result.type,
          moduleId: result.id,
          moduleName: result.name ?? '',
        }
      : null
    setLinkedModule(next)
    // Desktop persists mid-recording changes on the Rust side; the web
    // recorder sends the link with the upload instead.
    if (isDesktop && isActive) {
      void updateRecordingLink(
        next?.moduleType ?? null,
        next?.moduleId ?? null,
      ).catch(() => {})
    }
  }

  const handleTemplateChange = (templateRef: string | null) => {
    setSummaryTemplate(templateRef)
    // Desktop persists mid-recording changes on the Rust side; the web
    // recorder sends the template with the upload instead.
    if (isDesktop && isActive) {
      void updateRecordingTemplate(templateRef).catch(() => {})
    }
    // Recording was started from a calendar meeting — remember the choice as
    // that meeting series' default.
    if (activeEventKey) {
      void meetingSummaryTemplatesApi
        .setPreference(activeEventKey, templateRef)
        .then(() => toast.success('Saved as the default for this meeting'))
        .catch(() => {})
    }
  }

  const syncWebState = (
    state: 'idle' | 'recording' | 'paused' | 'finalizing',
    durationMs = webRecorderRef.current?.durationMs ?? 0,
  ) => {
    applyStatePayload({
      state,
      recordingId:
        state === 'idle' ? undefined : (webRecordingIdRef.current ?? undefined),
      startedAt: undefined,
      durationMs,
      pausedMs: 0,
    })
  }

  const handleWebStart = async () => {
    const recorder = new WebMicRecorder({
      onLevel: level => setLevels({ mic: level, system: 0 }),
      onDuration: durationMs => {
        syncWebState('recording', durationMs)
        // Auto-stop at the session limit so a forgotten recording can't run
        // all day. The audio so far is still uploaded and processed.
        if (durationMs >= MAX_RECORDING_MS && webRecorderRef.current) {
          toast.info('Recording reached the 2-hour limit', {
            description:
              'Stopping and generating notes from what was captured.',
          })
          void handleStop()
        }
      },
    })
    try {
      await recorder.start()
    } catch (error) {
      const isDenied =
        error instanceof DOMException &&
        (error.name === 'NotAllowedError' || error.name === 'SecurityError')
      toast.error(
        isDenied ? 'Microphone access denied' : 'Could not start recording',
        {
          description: isDenied
            ? 'Allow microphone access for this site in your browser settings, then try again.'
            : String(error instanceof Error ? error.message : error).slice(
                0,
                200,
              ),
        },
      )
      return
    }
    webRecorderRef.current = recorder
    // A real UUID doubles as the scratchpad image upload scope. The previous
    // static "web" id could not be stored in Document.moduleId (UUID).
    webRecordingIdRef.current = crypto.randomUUID()
    syncWebState('recording', 0)
  }

  const handleWebStop = async () => {
    const recorder = webRecorderRef.current
    if (!recorder) return
    const webRecordingId = webRecordingIdRef.current
    webRecorderRef.current = null
    syncWebState('finalizing')
    try {
      const { blob, mimeType, durationMs } = await recorder.stop()
      const durationSeconds = Math.max(1, Math.round(durationMs / 1000))
      const extension = fileExtensionForMimeType(mimeType)
      const upload = meetingRecordingsApi
        .uploadAudioBlob(blob, {
          fileName: `recording-${Date.now()}.${extension}`,
          mimeType: mimeType.split(';')[0],
          durationSeconds,
          moduleType: linkedModule?.moduleType,
          moduleId: linkedModule?.moduleId,
          summaryTemplate:
            useRecordingStore.getState().summaryTemplate ?? undefined,
          userNotes:
            useRecordingStore.getState().scratchpad.trim() || undefined,
        })
        .then(result => {
          if (webRecordingId) clearStoredScratchpad(webRecordingId)
          // Make the new recording show up immediately on the Meetings tab
          void queryClient.invalidateQueries({
            queryKey: ['meeting-recordings'],
          })
          return result
        })
        .finally(() => {
          syncWebState('idle')
          webRecordingIdRef.current = null
        })
      toast.promise(upload, {
        loading: 'Uploading recording…',
        success: 'Recording uploaded — generating AI notes…',
        error: error =>
          `Upload failed: ${String(error instanceof Error ? error.message : error).slice(0, 160)}`,
      })
      await upload
    } catch {
      // toast.promise already surfaced the failure
      syncWebState('idle')
      webRecordingIdRef.current = null
    }
  }

  const needsSystemAudioPrompt = async () => {
    if (systemAudioModeRef.current !== 'tap') return
    const status = await getSystemAudioStatus().catch(() => 'undetermined')
    return status === 'undetermined'
  }

  const handleStart = async () => {
    setIsBusy(true)
    try {
      if (!isDesktop) {
        await handleWebStart()
        setIsOpen(false)
        return
      }
      const permissions = await checkRecordingPermissions()
      if (permissions.microphone !== 'granted') {
        setShowPermissionsDialog(true)
        return
      }
      if (await needsSystemAudioPrompt()) {
        setShowPermissionsDialog(true)
        return
      }
      await startRecording({
        moduleType: linkedModule?.moduleType,
        moduleId: linkedModule?.moduleId,
        summaryTemplate: summaryTemplate ?? undefined,
        // Fresh read, not the cached checkbox state — captionModel is null
        // until the on-mount fetch resolves, which used to silently start
        // captionless right after a window (re)mount.
        liveCaptions: await resolveLiveCaptionsEnabled(),
      })
      setIsOpen(false)
    } catch (error) {
      toast.error('Could not start recording', {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 200),
      })
    } finally {
      setIsBusy(false)
    }
  }

  const handlePause = () => {
    if (isDesktop) {
      void pauseRecording()
    } else {
      webRecorderRef.current?.pause()
      syncWebState('paused')
    }
  }

  const handleResume = () => {
    if (isDesktop) {
      void resumeRecording()
    } else {
      webRecorderRef.current?.resume()
      syncWebState('recording')
    }
  }

  const handleStop = async () => {
    setIsBusy(true)
    try {
      if (!isDesktop) {
        setIsOpen(false)
        await handleWebStop()
        return
      }
      await stopRecording()
      setIsOpen(false)
      toast.success('Recording stopped', {
        description: 'Uploading and generating AI notes…',
      })
    } catch (error) {
      toast.error('Could not stop recording', {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 200),
      })
    } finally {
      setIsBusy(false)
    }
  }

  // Discard the in-progress recording without uploading or generating notes
  // (the audio is thrown away) — like deleting an active timer.
  const handleDiscard = async () => {
    setIsBusy(true)
    try {
      if (!isDesktop) {
        const activeId = webRecordingIdRef.current
        webRecorderRef.current?.cancel()
        webRecorderRef.current = null
        if (activeId) clearStoredScratchpad(activeId)
        syncWebState('idle')
        webRecordingIdRef.current = null
        setIsOpen(false)
        toast.success('Recording discarded')
        return
      }
      // Flag the active recording so the auto-uploader skips it and deletes
      // the local file once the worker finishes finalizing.
      const activeId = useRecordingStore.getState().recordingId
      if (activeId) useRecordingStore.getState().markDiscarded(activeId)
      await stopRecording()
      setIsOpen(false)
      toast.success('Recording discarded')
    } catch (error) {
      toast.error('Could not discard recording', {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 200),
      })
    } finally {
      setIsBusy(false)
    }
  }

  // Expose session controls so the live recording panel drives the SAME
  // handlers (the web recorder instance lives in this component). Declared
  // AFTER the handlers — the compiler reads them at render time, so placing
  // this earlier hits their temporal dead zone. Re-registered every render
  // to keep closures fresh.
  useEffect(() => {
    useRecordingStore.getState().registerControls({
      pause: handlePause,
      resume: handleResume,
      stop: handleStop,
      discard: handleDiscard,
    })
  })

  // After all hooks (rules-of-hooks) — unsupported platform or flag off
  if (!isSupported || !checkFeatureFlag('MEETING_NOTES')) {
    return null
  }

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-8 gap-1.5 border px-3 text-sm transition-all duration-200 hover:scale-105 hover:shadow-md',
              useDarkText
                ? 'border-black/30 bg-black/5 text-black hover:bg-black/10'
                : 'border-white/30 bg-white/5 text-white hover:bg-white/10',
              isActive && 'text-red-500',
            )}
            title={isActive ? 'Recording meeting…' : 'Record meeting'}
          >
            {state === 'finalizing' ? (
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
            ) : isActive ? (
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  isRecording && 'animate-pulse bg-red-500',
                  isPaused && 'bg-amber-500',
                )}
              />
            ) : (
              <RecordIcon className="h-4 w-4" />
            )}
            {isActive ? (
              <span className="font-mono text-xs tabular-nums">
                {formatElapsed(durationMs)}
              </span>
            ) : (
              <span>Record</span>
            )}
            {isRecording && (
              <VuBars level={Math.max(levels.mic, levels.system)} />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[min(22rem,calc(100vw-2rem))] p-4"
          align="end"
          onOpenAutoFocus={event => event.preventDefault()}
        >
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium">
                {isActive ? 'Recording meeting' : 'Record meeting'}
              </p>
              {isActive && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground"
                  onClick={() => {
                    useRecordingStore.getState().setLivePanelOpen(true)
                    setIsOpen(false)
                  }}
                >
                  Open panel
                </Button>
              )}
              {isActive && (
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      isRecording && 'animate-pulse bg-red-500',
                      isPaused && 'bg-amber-500',
                      state === 'finalizing' && 'bg-muted-foreground',
                    )}
                  />
                  <span className="font-mono text-sm tabular-nums">
                    {formatElapsed(durationMs)}
                  </span>
                </div>
              )}
            </div>

            {isActive && (
              <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <span className="w-9">You</span>
                  <VuBars level={levels.mic} />
                </div>
                {/* "Others" reflects system audio — only meaningful on desktop
                    with system-audio capture; stays flat for mic-only/web. */}
                <div
                  className={cn(
                    'flex items-center gap-1.5',
                    systemAudioCaptured === false && 'opacity-40',
                  )}
                >
                  <span className="w-12">Others</span>
                  <VuBars level={levels.system} />
                </div>
              </div>
            )}

            {!isDesktop && (
              <p className="rounded-md bg-muted px-2.5 py-2 text-[11px] leading-snug text-muted-foreground">
                Web records your mic only — use the desktop app to capture other
                participants.
              </p>
            )}

            {isDesktop && isActive && systemAudioCaptured === false && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300">
                Mic only — system audio is off, so other participants aren’t
                being captured.
              </p>
            )}

            <div className="space-y-1.5">
              <LinkWorkSelector
                label="Add note to"
                value={linkValue}
                onSelect={handleModuleSelect}
                compact
                closeOnSelect
                portalToBody
              />
              <p className="text-[11px] leading-snug text-muted-foreground">
                {linkedModule ? 'Added to this record.' : 'Saved to My Notes.'}
              </p>
            </div>

            <div className="space-y-1.5">
              <SummaryTemplateSelect
                value={summaryTemplate}
                onChange={handleTemplateChange}
                disabled={state === 'finalizing'}
              />
              <p className="text-[11px] leading-snug text-muted-foreground">
                {activeEventKey
                  ? 'Changing this sets the default for this meeting series.'
                  : 'Shapes how the AI writes the notes.'}
              </p>
            </div>

            {isDesktop && captionModel?.supported && !isActive && (
              <label className="flex cursor-pointer items-center gap-2">
                <Checkbox
                  checked={
                    liveCaptionsEnabled && captionModel.state === 'ready'
                  }
                  disabled={captionDownloadPct !== null}
                  onCheckedChange={checked =>
                    void handleCaptionsToggle(checked === true)
                  }
                />
                <span className="text-sm">Live captions</span>
                <span className="text-[11px] text-muted-foreground">
                  {captionDownloadPct !== null
                    ? `Downloading model… ${captionDownloadPct}%`
                    : captionModel.state !== 'ready'
                      ? 'On-device, one-time 142MB download'
                      : 'On-device, free'}
                </span>
              </label>
            )}

            {isActive && captionsActive && (
              <div className="space-y-1.5">
                <div className="max-h-24 space-y-1 overflow-y-auto rounded-md bg-muted/60 px-2.5 py-2">
                  {captions.length === 0 ? (
                    <p className="text-[11px] italic text-muted-foreground">
                      {captionsActive === 'starting'
                        ? 'Starting live captions… loading the speech model.'
                        : 'Listening… captions appear a few seconds behind.'}
                    </p>
                  ) : (
                    captions.slice(-3).map(caption => (
                      <p
                        key={caption.startMs}
                        className="text-xs leading-snug text-foreground/90"
                      >
                        {caption.text}
                      </p>
                    ))
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-1.5"
                  disabled={
                    captions.length === 0 || catchUpCredits?.canUse === false
                  }
                  onClick={() => void handleCatchUp()}
                >
                  Catch me up
                </Button>
                <AICreditNotice status={catchUpCredits} compact />
              </div>
            )}

            {isActive && (
              <div className="space-y-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-1.5"
                  onClick={() => {
                    useRecordingStore.getState().setLivePanelOpen(true)
                    setIsOpen(false)
                  }}
                >
                  <PencilSimpleLineIcon className="h-3.5 w-3.5" />
                  {scratchpad.trim() ? 'Continue notes in panel' : 'Take notes'}
                </Button>
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Your notes stay yours — they lead the summary and are kept
                  verbatim as a page.
                </p>
              </div>
            )}

            {isActive ? (
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                  disabled={isBusy || state === 'finalizing'}
                  onClick={() => void handleDiscard()}
                  title="Discard recording (audio is deleted, no notes generated)"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
                {isPaused ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1.5"
                    disabled={isBusy}
                    onClick={handleResume}
                  >
                    <PlayIcon className="h-3.5 w-3.5" />
                    Resume
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1.5"
                    disabled={isBusy || state === 'finalizing'}
                    onClick={handlePause}
                  >
                    <PauseIcon className="h-3.5 w-3.5" />
                    Pause
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  className="flex-1 gap-1.5"
                  disabled={isBusy || state === 'finalizing'}
                  onClick={() => void handleStop()}
                >
                  <StopIcon className="h-3.5 w-3.5" weight="fill" />
                  Stop
                </Button>
              </div>
            ) : (
              <Button
                className="w-full gap-2"
                disabled={isBusy}
                onClick={() => void handleStart()}
              >
                {isBusy ? (
                  <CircleNotchIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <RecordIcon className="h-4 w-4" weight="fill" />
                )}
                Start recording
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {isDesktop && (
        <RecordingPermissionsDialog
          open={showPermissionsDialog}
          onOpenChange={setShowPermissionsDialog}
          onGranted={() => {
            setShowPermissionsDialog(false)
            void handleStart()
          }}
        />
      )}

      <Dialog open={catchUpOpen} onOpenChange={setCatchUpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Catch me up</DialogTitle>
            <DialogDescription>
              What you missed in this meeting so far.
            </DialogDescription>
          </DialogHeader>
          {isCatchingUp ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
              Summarizing the conversation…
            </div>
          ) : (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {catchUpSummary}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
